import { useMemo } from 'react';
import { FormFactor, GpuSpecs } from '../../../types/configurator';
import { GPU_CATALOG, getGpusForFormFactor } from '../../../data/scenarios';

interface GpuSelectorProps {
  selectedGpu: GpuSpecs | undefined;
  formFactor: FormFactor;
  onSelect: (gpu: GpuSpecs | undefined) => void;
  disabled?: boolean;
  gpuSlots?: number;  // Number of GPU slots available (0 = no GPU support)
}

// Helper to find GPU key by matching the GpuSpecs object
function findGpuKey(gpu: GpuSpecs | undefined): string {
  if (!gpu) return 'none';
  const entry = Object.entries(GPU_CATALOG).find(
    ([_, g]) => g.model === gpu.model && g.vendor === gpu.vendor
  );
  return entry ? entry[0] : 'none';
}

export function GpuSelector({ selectedGpu, formFactor, onSelect, disabled, gpuSlots }: GpuSelectorProps) {
  // Check if hardware supports GPUs
  const supportsGpu = gpuSlots === undefined || gpuSlots > 0;

  // Get GPUs filtered by form factor and group by vendor
  const gpusByVendor = useMemo(() => {
    if (!supportsGpu) {
      return { nvidia: [], amd: [], intel: [] };
    }
    const availableGpus = getGpusForFormFactor(formFactor);

    // Create a map of GPU key -> GpuSpecs for available GPUs
    const availableKeys = new Set(
      Object.entries(GPU_CATALOG)
        .filter(([_, gpu]) => availableGpus.some(a => a.model === gpu.model))
        .map(([key]) => key)
    );

    // Group by vendor
    const grouped: Record<string, { key: string; gpu: GpuSpecs }[]> = {
      nvidia: [],
      amd: [],
      intel: [],
    };

    Object.entries(GPU_CATALOG).forEach(([key, gpu]) => {
      if (availableKeys.has(key) && grouped[gpu.vendor]) {
        grouped[gpu.vendor].push({ key, gpu });
      }
    });

    // Sort each vendor's GPUs by tier, then price
    const tierOrder = ['edge', 'entry', 'desktop', 'workstation', 'datacenter'];
    Object.values(grouped).forEach(gpus => {
      gpus.sort((a, b) => {
        const tierDiff = tierOrder.indexOf(a.gpu.tier) - tierOrder.indexOf(b.gpu.tier);
        if (tierDiff !== 0) return tierDiff;
        return a.gpu.price - b.gpu.price;
      });
    });

    return grouped;
  }, [formFactor, supportsGpu]);

  const hasAnyGpus = supportsGpu && Object.values(gpusByVendor).some(arr => arr.length > 0);

  const formatGpuLabel = (gpu: GpuSpecs): string => {
    return `${gpu.model} (${gpu.vramGB}GB, ${gpu.tdpWatts}W, $${gpu.price})`;
  };

  const vendorLabels: Record<string, string> = {
    nvidia: 'NVIDIA',
    amd: 'AMD',
    intel: 'Intel',
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700">
        GPU (Optional)
      </label>
      <select
        value={findGpuKey(selectedGpu)}
        onChange={(e) => {
          const key = e.target.value;
          if (key === 'none') {
            onSelect(undefined);
          } else {
            onSelect(GPU_CATALOG[key]);
          }
        }}
        disabled={disabled || !hasAnyGpus}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
      >
        <option value="none">No GPU</option>
        {Object.entries(gpusByVendor).map(([vendor, gpus]) => {
          if (gpus.length === 0) return null;
          return (
            <optgroup key={vendor} label={vendorLabels[vendor]}>
              {gpus.map(({ key, gpu }) => (
                <option key={key} value={key}>
                  {formatGpuLabel(gpu)}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
      {selectedGpu && (
        <p className="mt-1 text-xs text-gray-500">
          {selectedGpu.vendor === 'nvidia' && selectedGpu.computeCapability && (
            <span>CUDA {selectedGpu.computeCapability} | </span>
          )}
          {selectedGpu.vendor === 'amd' && selectedGpu.rocmSupport && (
            <span>ROCm supported | </span>
          )}
          Adds {selectedGpu.tdpWatts}W to power budget
        </p>
      )}
      {!supportsGpu && (
        <p className="mt-1 text-xs text-amber-600">
          This hardware does not support discrete GPUs
        </p>
      )}
      {supportsGpu && !hasAnyGpus && (
        <p className="mt-1 text-xs text-amber-600">
          No GPUs available for this form factor
        </p>
      )}
    </div>
  );
}
